import type { UseMutationResult } from '@tanstack/react-query';
import { ZfCheckbox } from '@zafire/react';
import type { CellClassParams, ColDef, ICellRendererParams, ITooltipParams, ValueFormatterParams } from 'ag-grid-enterprise';
import type { AxiosError } from 'axios';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient, useSurplusSettings, useUpdateSurplusItem } from '../../api';
import type { ItemSettingsDto } from '../../api/generated/client';
import { DEFAULT_INPUT_DATE_FORMAT, INCOME_EXPENSE_INPUT_DATE_FORMAT } from '../../config/constants';
import { CashOccurrenceType } from '../../enums';
import type { ISetting } from '../../interfaces/simulation-settings';
import { formatAmount } from '../../utils/renderUtils';
import { AgGridTable, OverflowTooltip } from '../atoms';
import { RemarkTooltip } from './RemarkTooltip';
import { SettingBoxEmptyState } from './SettingBoxEmptyState';

const getAmountColor = (amount: number): string | undefined => {
    if (amount > 0) return 'var(--fg-color-success-normal)';
    if (amount < 0) return 'var(--fg-color-danger-normal)';

    return undefined;
};

interface IProps {
    cashOccurrenceType: CashOccurrenceType;
    cashflowItems: ItemSettingsDto[];
    updateItemSettings: UseMutationResult<ISetting, AxiosError, ISetting>;
    enabled: boolean;
    showToDateColumn?: boolean;
    showEmptyState?: boolean;
    isUpdating?: boolean;
    // When rendered inside the liquidity calculator, surplus items carry their own selection
    // (`liquidityEnabled`), independent of the projection `enabled` flag.
    isLiquiditySurplus?: boolean;
}

export const SettingBoxCashflowTable: FunctionComponent<IProps> = ({
    cashOccurrenceType,
    cashflowItems,
    updateItemSettings,
    enabled,
    showToDateColumn,
    showEmptyState,
    isUpdating,
    isLiquiditySurplus,
}) => {
    const { t, i18n } = useTranslation();
    const { data: client } = useClient();
    const { data: surplusSettings } = useSurplusSettings(cashOccurrenceType === CashOccurrenceType.RECURRING);
    const updateSurplusItem = useUpdateSurplusItem();

    const [localStateCashFlowItems, setCashFlowItems] = useState(cashflowItems);

    useEffect(() => setCashFlowItems(cashflowItems), [cashflowItems]);

    const setChecked = (id: number, enabled: boolean) => {
        setCashFlowItems((prevState) => prevState.map((item) => (item.id === id ? { ...item, enabled } : item)));
    };

    const isSurplusOverrideEnabled = useCallback(
        () => surplusSettings?.enabled && cashOccurrenceType === CashOccurrenceType.RECURRING,
        [surplusSettings?.enabled, cashOccurrenceType],
    );

    // In the liquidity calculator, surplus is expanded server-side into standard per-year recurring
    // items, so selection always uses the regular `enabled` flag (same as projection).
    const isItemChecked = useCallback((entry: ItemSettingsDto) => !!entry.enabled, []);

    const getAmount = useCallback(
        (entry: ItemSettingsDto) => (isSurplusOverrideEnabled() && entry.surplusType === 'EXPENSE' ? entry.amount! * -1 : entry.amount!),
        [isSurplusOverrideEnabled],
    );
    const getName = useCallback(
        (entry: ItemSettingsDto) =>
            isSurplusOverrideEnabled() ? (entry.surplusType === 'INCOME' ? t('incomeSurplus') : t('expenseSurplus')) : (entry.name ?? ''),
        [isSurplusOverrideEnabled, t],
    );

    const onCheckBoxClick = useCallback(
        (item: ItemSettingsDto, checked: boolean) => {
            // Projection surplus selection lives on the surplus items themselves; the liquidity panel
            // consumes its own endpoint (surplus already expanded per year), so it uses the context item.
            if (isSurplusOverrideEnabled() && !isLiquiditySurplus) {
                const toggledSurplusItem = surplusSettings?.itemsSettings!.find((surplusItem) => surplusItem.id === item.id);
                updateSurplusItem.mutate({ ...toggledSurplusItem!, enabled: checked });
            } else {
                setChecked(item.id!, checked);
                updateItemSettings.mutate(
                    { ...item, enabled: checked },
                    {
                        onError: () => {
                            setChecked(item.id!, !checked);
                        },
                    },
                );
            }
        },
        [surplusSettings?.itemsSettings, isSurplusOverrideEnabled, isLiquiditySurplus, updateItemSettings, updateSurplusItem],
    );

    const tableData: ItemSettingsDto[] = useMemo(
        () => localStateCashFlowItems.filter((item) => !item.hidden).map((item) => ({ ...item, currency: client?.currency ?? '' })),
        [localStateCashFlowItems, client?.currency],
    );

    const isRecurring = cashOccurrenceType === CashOccurrenceType.RECURRING;
    const showFromToColumns = isRecurring && !!showToDateColumn;

    // The liquidity calculator renders surplus ranges as month/year only; projection keeps the full day precision.
    const surplusDateFormat = isLiquiditySurplus ? INCOME_EXPENSE_INPUT_DATE_FORMAT : DEFAULT_INPUT_DATE_FORMAT;

    const getFromDate = (data: ItemSettingsDto) => {
        if (isSurplusOverrideEnabled()) {
            return moment(data?.fromDate).format(surplusDateFormat);
        }

        if (cashOccurrenceType === CashOccurrenceType.ONE_OFF) {
            return moment(data?.effectiveDate).format(DEFAULT_INPUT_DATE_FORMAT);
        }

        // Recurring cashflows with an incomplete date range are enforce-disabled and may miss a start
        // date — render those blank instead of an "Invalid date".
        return data?.fromDate ? moment(data.fromDate).format(INCOME_EXPENSE_INPUT_DATE_FORMAT) : '';
    };

    // Open-ended recurring cashflows have no end date — render those blank instead of an "Invalid date".
    const getToDate = (data: ItemSettingsDto) =>
        data?.toDate ? moment(data.toDate).format(isSurplusOverrideEnabled() ? surplusDateFormat : INCOME_EXPENSE_INPUT_DATE_FORMAT) : '';

    // Enforce-disabled rows expose their localized remark as a whole-row tooltip (all columns but the name,
    // which keeps its own overflow/full-name tooltip).
    const enforceDisabledRemark = ({ data }: ITooltipParams<ItemSettingsDto>) =>
        data?.enforceDisable ? (data.remarks?.[i18n.language] ?? data.remarks?.EN) : undefined;

    const columnDefs: ColDef<ItemSettingsDto>[] = [
        {
            width: 40,
            tooltipValueGetter: enforceDisabledRemark,
            cellRenderer: ({ data }: ICellRendererParams<ItemSettingsDto>) => (
                <ZfCheckbox
                    checked={data ? isItemChecked(data) : false}
                    disabled={!enabled || data?.enforceDisable}
                    onCheckboxCheck={(event: CustomEvent<boolean>) => data && onCheckBoxClick(data, event.detail)}
                />
            ),
        },
        {
            field: 'name',
            headerName: t('name'),
            width: 125,
            valueFormatter: ({ data }: ValueFormatterParams<ItemSettingsDto>) => (data ? getName(data) : ''),
            cellRenderer: ({ valueFormatted }: ICellRendererParams<ItemSettingsDto>) => (
                <OverflowTooltip text={valueFormatted ?? ''} width={125} padding={24} />
            ),
        },
        {
            field: 'fromDate',
            headerName: t(showFromToColumns ? 'from' : isRecurring ? 'fromDate' : 'effectiveDate'),
            width: showFromToColumns ? 72 : 105,
            tooltipValueGetter: enforceDisabledRemark,
            valueFormatter: ({ data }: ValueFormatterParams<ItemSettingsDto>) => getFromDate(data!),
        },
        ...(showFromToColumns
            ? [
                  {
                      field: 'toDate',
                      headerName: t('to'),
                      width: 72,
                      tooltipValueGetter: enforceDisabledRemark,
                      valueFormatter: ({ data }: ValueFormatterParams<ItemSettingsDto>) => getToDate(data!),
                  } satisfies ColDef<ItemSettingsDto>,
              ]
            : []),
        {
            field: 'currency',
            headerName: t('Currency'),
            width: 80,
            tooltipValueGetter: enforceDisabledRemark,
        },
        {
            field: 'amount',
            headerName: t('Amount'),
            minWidth: 100,
            flex: 1,
            headerClass: 'ag-right-aligned-header',
            tooltipValueGetter: enforceDisabledRemark,
            cellStyle: ({ data }: CellClassParams<ItemSettingsDto>) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'end',
                textAlign: 'right',
                ...(data ? { color: getAmountColor(getAmount(data)) } : {}),
            }),
            valueFormatter: ({ data }: ValueFormatterParams<ItemSettingsDto, number>) => (data ? formatAmount(getAmount(data), true) : ''),
        },
    ];

    if (tableData.length === 0 && showEmptyState)
        return (
            <SettingBoxEmptyState
                message={t(isRecurring ? 'settingsDrawerEmpty.recurringCashflows' : 'settingsDrawerEmpty.oneOffCashflows')}
                showWealthOverviewLink={true}
                showEventsLink={!isRecurring}
            />
        );

    return (
        <div style={{ opacity: isUpdating ? 0.6 : 1, transition: 'opacity 150ms ease-in-out' }}>
            <AgGridTable
                domLayout="autoHeight"
                rowData={tableData}
                columnDefs={columnDefs}
                getRowId={({ data }) => (data.id != null ? String(data.id) : String(data.name))}
                tooltipShowDelay={300}
                getRowStyle={({ data }) => (data?.enforceDisable ? { opacity: 0.5 } : undefined)}
                defaultColDef={{
                    resizable: false,
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    tooltipComponent: RemarkTooltip,
                    cellStyle: { display: 'flex', alignItems: 'center' },
                }}
                tableRowHeight={45}
                cellHorizontalPadding={12}
            />
        </div>
    );
};
